import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Tip } from '@/components/ui/tip';
import { disconnectWesSocket, getWesSocket } from '@/lib/wesSocket';

const ACTIVE_STATUSES = new Set(['assigned', 'in_progress', 'blocked']);

type WesWorkUnit = {
  id: string;
  orderId: string;
  title: string;
  status: string;
  startedAt?: string | null;
  elapsedSeconds?: number;
  slaSeconds?: number;
  slaStatus?: 'ok' | 'warning' | 'late';
  operatorFirstName?: string | null;
  operatorLastName?: string | null;
  order?: {
    appointmentCode?: string | null;
    serviceType?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    companyName?: string | null;
    licensePlate?: string | null;
    make?: string | null;
    model?: string | null;
  } | null;
};

const statusClass = (status?: string) => {
  switch (status) {
    case 'in_progress':
      return 'bg-blue-100 text-blue-700';
    case 'blocked':
      return 'bg-red-100 text-red-700';
    case 'completed':
      return 'bg-emerald-100 text-emerald-700';
    case 'assigned':
      return 'bg-amber-100 text-amber-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const WesGeneral: React.FC = () => {
  const [workUnits, setWorkUnits] = useState<WesWorkUnit[]>([]);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [tick, setTick] = useState(Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const formatSeconds = (value?: number | null) => {
    const total = Math.max(0, Math.floor(value || 0));
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const getElapsedForItem = (item: WesWorkUnit) => {
    const base = Math.max(0, item.elapsedSeconds || 0);
    if (item.status !== 'in_progress') return base;
    if (!item.startedAt) return base;
    const startedAtMs = new Date(item.startedAt).getTime();
    if (Number.isNaN(startedAtMs)) return base;
    return Math.max(0, Math.floor((tick - startedAtMs) / 1000));
  };

  const fetchOverview = useCallback(async () => {
    try {
      const response = await axios.get('/api/wes/supervisor/overview');
      const units: WesWorkUnit[] = response.data.workUnits || [];
      setWorkUnits(units);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al cargar tablero WES');
    }
  }, []);

  useEffect(() => {
    fetchOverview();
    const socket = getWesSocket();
    const handleUpdate = () => fetchOverview();
    socket.on('wes:update', handleUpdate);
    return () => {
      socket.off('wes:update', handleUpdate);
      disconnectWesSocket();
    };
  }, [fetchOverview]);

  const handleComplete = useCallback(async (workUnitId: string) => {
    setActionLoading(workUnitId);
    try {
      await axios.post(`/api/wes/work-units/${workUnitId}/complete`);
      toast.success('Tarea completada');
      fetchOverview();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'No se pudo completar la tarea');
    } finally {
      setActionLoading(null);
    }
  }, [fetchOverview]);

  const handleStart = useCallback(async (workUnitId: string) => {
    setActionLoading(workUnitId);
    try {
      await axios.post(`/api/wes/work-units/${workUnitId}/start`);
      toast.success('Tarea iniciada');
      fetchOverview();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'No se pudo iniciar la tarea');
    } finally {
      setActionLoading(null);
    }
  }, [fetchOverview]);

  const handleBlock = useCallback(async (workUnitId: string) => {
    setActionLoading(workUnitId);
    try {
      await axios.post(`/api/wes/work-units/${workUnitId}/block`, {
        reason: 'Bloqueado desde taller general'
      });
      toast.success('Tarea bloqueada');
      fetchOverview();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'No se pudo bloquear la tarea');
    } finally {
      setActionLoading(null);
    }
  }, [fetchOverview]);

  const handleUnblock = useCallback(async (workUnitId: string) => {
    setActionLoading(workUnitId);
    try {
      await axios.post(`/api/wes/work-units/${workUnitId}/unblock`, {
        reason: 'Reanudado desde taller general'
      });
      toast.success('Tarea reanudada');
      fetchOverview();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'No se pudo reanudar la tarea');
    } finally {
      setActionLoading(null);
    }
  }, [fetchOverview]);

  const orders = useMemo(() => {
    const dedupeByTitle = (items: WesWorkUnit[]) => {
      const priority: Record<string, number> = { in_progress: 0, blocked: 1, assigned: 2, pending: 3 };
      const keep = new Map<string, WesWorkUnit>();
      items.forEach((item) => {
        const key = String(item.title || '').toLowerCase().trim();
        const existing = keep.get(key);
        if (!existing) {
          keep.set(key, item);
          return;
        }
        const pExisting = priority[existing.status] ?? 99;
        const pCurrent = priority[item.status] ?? 99;
        if (pCurrent < pExisting) keep.set(key, item);
      });
      return Array.from(keep.values());
    };

    const grouped = new Map<string, WesWorkUnit[]>();
    workUnits.forEach((wu) => {
      const list = grouped.get(wu.orderId) || [];
      list.push(wu);
      grouped.set(wu.orderId, list);
    });
    return Array.from(grouped.entries())
      .map(([orderId, items]) => {
      const order = items.find((wu) => wu.order)?.order || null;
      const activeItems = dedupeByTitle(items.filter((item) => ACTIVE_STATUSES.has(item.status)));
      const totalElapsedSeconds = activeItems.reduce((acc, item) => acc + getElapsedForItem(item), 0);
      const totalSlaSeconds = activeItems.reduce((acc, item) => acc + Math.max(0, item.slaSeconds || 0), 0);
      return { orderId, order, items: activeItems, totalElapsedSeconds, totalSlaSeconds };
      })
      .filter((order) => order.items.some((item) => ACTIVE_STATUSES.has(item.status)));
  }, [workUnits, tick]);

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Taller - Vista General</h1>
          <p className="text-sm text-muted-foreground">Panel general de OTs activas</p>
        </div>

        {orders.length === 0 ? (
          <Card className="border-2 border-muted">
            <CardContent className="p-8 text-center text-muted-foreground">
              No hay OTs con tareas activas.
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {orders.map((order) => {
              const clientName = order.order?.companyName
                ? order.order.companyName
                : `${order.order?.firstName || ''} ${order.order?.lastName || ''}`.trim();
              const vehicleLabel = [order.order?.make, order.order?.model].filter(Boolean).join(' ');
              const plate = order.order?.licensePlate || '';
              return (
                <Card key={order.orderId} className="border-2 border-muted">
                  <CardContent className="p-5 space-y-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <p className="text-lg font-semibold">
                          {order.order?.appointmentCode || order.order?.serviceType || order.orderId}
                        </p>
                      </div>
                      <p className="text-sm text-muted-foreground">{clientName || 'Cliente sin asignar'}</p>
                      <p className="text-xs text-muted-foreground">
                        {[vehicleLabel, plate].filter(Boolean).join(' - ') || '-'}
                      </p>
                      {order.totalSlaSeconds > 0 && (
                        <p className="text-xs font-semibold text-muted-foreground">
                          Tiempo OT: {formatSeconds(order.totalElapsedSeconds)} / {formatSeconds(order.totalSlaSeconds)}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      {order.items
                        .filter((item) => ACTIVE_STATUSES.has(item.status))
                        .map((item) => {
                        const operatorName = [item.operatorFirstName, item.operatorLastName].filter(Boolean).join(' ');
                        const isBusy = actionLoading === item.id;
                        const canStart = item.status === 'assigned';
                        const canComplete = item.status === 'in_progress';
                        const canBlock = item.status === 'in_progress' || item.status === 'assigned';
                        const canUnblock = item.status === 'blocked';
                        return (
                          <div key={item.id} className="space-y-2 rounded-md border border-muted/70 p-3">
                            <div className="flex items-center justify-between gap-3">
                              <div>
                              <p className="text-sm font-medium">{item.title}</p>
                              <p className="text-xs text-muted-foreground">
                                {operatorName || 'Sin asignar'}
                              </p>
                              {(item.slaSeconds || 0) > 0 && (
                                <p className="text-xs font-semibold text-muted-foreground">
                                  Tiempo servicio: {formatSeconds(getElapsedForItem(item))} / {formatSeconds(item.slaSeconds || 0)}
                                </p>
                              )}
                              </div>
                            <span className={`rounded-full px-2 py-1 text-xs font-semibold ${statusClass(item.status)}`}>
                              {item.status}
                            </span>
                          </div>
                            <div className="flex flex-wrap gap-2">
                              {canStart && (
                                <button
                                  type="button"
                                  className={`rounded-md border px-3 py-1 text-xs font-semibold transition ${isBusy ? 'opacity-50 cursor-not-allowed' : 'hover:bg-muted'}`}
                                  onClick={() => handleStart(item.id)}
                                  disabled={isBusy}
                                >
                                  Iniciar
                                </button>
                              )}
                              <button
                                type="button"
                                className={`rounded-md border px-3 py-1 text-xs font-semibold transition ${canComplete ? 'hover:bg-muted' : 'opacity-50 cursor-not-allowed'}`}
                                onClick={() => handleComplete(item.id)}
                                disabled={!canComplete || isBusy}
                              >
                                Completar
                              </button>
                              <button
                                type="button"
                                className={`rounded-md border px-3 py-1 text-xs font-semibold text-rose-600 transition ${canBlock ? 'hover:bg-rose-50' : 'opacity-50 cursor-not-allowed'}`}
                                onClick={() => handleBlock(item.id)}
                                disabled={!canBlock || isBusy}
                              >
                                Bloquear
                              </button>
                              <button
                                type="button"
                                className={`rounded-md border px-3 py-1 text-xs font-semibold transition ${canUnblock ? 'hover:bg-muted' : 'opacity-50 cursor-not-allowed'}`}
                                onClick={() => handleUnblock(item.id)}
                                disabled={!canUnblock || isBusy}
                              >
                                Reanudar
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default WesGeneral;
